import React from 'react';
import styled from 'styled-components';

const CalendarPopup = styled.div`
  position: fixed;
  left: ${({ left }) => left}px;
  top: ${({ top }) => top}px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  z-index: 2000;
  padding: 12px 16px;
  min-width: 240px;
  transform: translateX(-50%);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #222;
  margin-bottom: 8px;
`;

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 32px);
  gap: 4px;
  width: fit-content;
`;

const WeekdayCell = styled.div`
  height: 22px;
  line-height: 22px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $weekdayIndex }) =>
    $weekdayIndex === 0 ? '#d32f2f' : $weekdayIndex === 6 ? '#1565c0' : '#888'};
`;

const CalendarPlaceholder = styled.div`
  width: 32px;
  height: 32px;
`;

const CalendarDay = styled.div`
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 8px;
  background: ${({ selected }) => (selected ? '#1976d2' : 'transparent')};
  color: ${({ selected }) => (selected ? '#fff' : '#222')};
  cursor: pointer;
  font-weight: ${({ selected }) => (selected ? 700 : 400)};
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #eae8e9;
    color: #1976d2;
  }
`;

const CloseButton = styled.button`
  font-size: 13px;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
`;

/**
 * `YYYY.MM.DD` 형태 날짜 파싱. 공백·한 자리 월일·마지막 점 허용.
 * 예: `2026.03.09`, `2026. 6. 5.`
 */
function parseDotDate(input) {
  if (!input || typeof input !== 'string') return null;
  const parts = input
    .trim()
    .split('.')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;

  const y = parseInt(parts[0], 10);
  const moNum = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  const mo = moNum - 1;

  if (parts[0].length !== 4 || mo < 0 || mo > 11 || day < 1) return null;
  const dim = new Date(y, mo + 1, 0).getDate();
  if (day > dim) return null;
  return { year: y, month: mo, day };
}

function formatDotDate(year, month0, day) {
  return `${year}.${String(month0 + 1).padStart(2, '0')}.${String(day).padStart(2, '0')}`;
}

function SimpleCalendar({ value, onSelect, onClose, anchorRef }) {
  const today = new Date();
  // 서울 타임존으로 현재 날짜 가져오기
  const seoulToday = new Date(today.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

  const trimmedValue = value != null && String(value).trim() !== '' ? String(value).trim() : '';
  const parsedFromValue = trimmedValue ? parseDotDate(trimmedValue) : null;
  const seoulYear = seoulToday.getFullYear();
  const seoulMonth = seoulToday.getMonth();

  const [year, setYear] = React.useState(
    parsedFromValue?.year ?? seoulYear
  );
  const [month, setMonth] = React.useState(
    parsedFromValue?.month ?? seoulMonth
  );

  React.useEffect(() => {
    const tv =
      value != null && String(value).trim() !== '' ? String(value).trim() : '';
    const p = tv ? parseDotDate(tv) : null;
    const st = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    if (p) {
      setYear(p.year);
      setMonth(p.month);
      return;
    }
    setYear(st.getFullYear());
    setMonth(st.getMonth());
  }, [value]);

  const defaultMinYear = seoulYear - 5;
  const defaultMaxYear = seoulYear + 4;
  const valueYearForRange = parsedFromValue?.year;
  const rangeMinYear = valueYearForRange != null
    ? Math.min(defaultMinYear, valueYearForRange)
    : defaultMinYear;
  const rangeMaxYear = valueYearForRange != null
    ? Math.max(defaultMaxYear, valueYearForRange)
    : defaultMaxYear;
  const years = Array.from(
    { length: rangeMaxYear - rangeMinYear + 1 },
    (_, i) => rangeMinYear + i
  );
  const months = Array.from({ length: 12 }, (_, i) => i);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthStartJsDay = new Date(year, month, 1).getDay();
  const leadingBlanks = monthStartJsDay;
  const totalDayCells = leadingBlanks + daysInMonth;
  const trailingBlanks = (7 - (totalDayCells % 7)) % 7;

  const todayDotStr = formatDotDate(
    seoulToday.getFullYear(),
    seoulToday.getMonth(),
    seoulToday.getDate()
  );
  const highlightDateStr =
    parsedFromValue != null
      ? formatDotDate(
          parsedFromValue.year,
          parsedFromValue.month,
          parsedFromValue.day
        )
      : todayDotStr;
  
  // 팝업 위치 계산
  const [popupPosition, setPopupPosition] = React.useState({ left: 0, top: 0 });
  
  React.useEffect(() => {
    if (!anchorRef?.current) return;
    
    const rect = anchorRef.current.getBoundingClientRect();
    console.log('simple calendarrect', rect);
    const popupHeight = 320;
    const popupWidth = 240;
    
    // 모달 헤더 높이 (대략 80px)를 고려하여 최소 top 위치 설정
    const minTop = 100;
    
    let top = rect.bottom + 10; // 기본적으로 아래쪽에 표시
    
    // 화면 하단에 공간이 부족한 경우 위쪽에 표시
    if (window.innerHeight - rect.bottom < popupHeight) {
      top = rect.top - popupHeight - 10;
    }
    
    // 모달 헤더에 가려지지 않도록 최소 top 위치 보장
    if (top < minTop) {
      top = minTop;
    }
    
    // 화면을 벗어나지 않도록 조정
    if (top + popupHeight > window.innerHeight - 20) {
      top = window.innerHeight - popupHeight - 20;
    }
    
    // 좌우 중앙 정렬
    let left = rect.left + (rect.width / 2);
    
    // 화면을 벗어나지 않도록 좌우 조정
    if (left - popupWidth / 2 < 20) {
      left = popupWidth / 2 + 20;
    } else if (left + popupWidth / 2 > window.innerWidth - 20) {
      left = window.innerWidth - popupWidth / 2 - 20;
    }
    
    setPopupPosition({ left, top });
  }, [anchorRef]);
  
  return (
    <CalendarPopup left={popupPosition.left} top={popupPosition.top}>
      <CalendarHeader>
        <select 
          value={year} 
          onChange={e => setYear(Number(e.target.value))} 
          style={{fontSize:15, marginRight:6}}
        >
          {years.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select 
          value={month} 
          onChange={e => setMonth(Number(e.target.value))} 
          style={{fontSize:15}}
        >
          {months.map(m => <option key={m} value={m}>{m+1}월</option>)}
        </select>
      </CalendarHeader>
      <CalendarGrid>
        {WEEKDAY_LABELS.map((label, idx) => (
          <WeekdayCell key={label} $weekdayIndex={idx}>{label}</WeekdayCell>
        ))}
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <CalendarPlaceholder key={`lead-${year}-${month}-${i}`} aria-hidden />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const d = i + 1;
          const dateStr = `${year}.${String(month + 1).padStart(2, '0')}.${String(d).padStart(2, '0')}`;
          return (
            <CalendarDay
              key={d}
              selected={highlightDateStr === dateStr}
              onClick={() => { onSelect(dateStr); onClose(); }}
            >
              {d}
            </CalendarDay>
          );
        })}
        {Array.from({ length: trailingBlanks }, (_, i) => (
          <CalendarPlaceholder key={`trail-${year}-${month}-${i}`} aria-hidden />
        ))}
      </CalendarGrid>
      <div style={{textAlign:'right', marginTop:8}}>
        <CloseButton onClick={onClose}>닫기</CloseButton>
      </div>
    </CalendarPopup>
  );
}

export default SimpleCalendar; 